import { useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Search, FileText, GitBranch, BarChart3, Download, Home, Menu } from 'lucide-react'
import { Button } from '@/components/ui/button.jsx'
import { Input } from '@/components/ui/input.jsx'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card.jsx'
import { Badge } from '@/components/ui/badge.jsx'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs.jsx'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet.jsx'
// import './App.css'

// API base URL - uses Vite environment variable
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5000/api'

// Mock data for demonstration since API might not be running
const mockStats = {
  total_policies: 20,
  policies_by_section: {
    'Governing Principles': 1,
    'Board Operations': 0,
    'Educational Program': 0,
    'Students': 0,
    'Community Relationships': 0,
    'Support Services': 0,
    'Personnel': 0,
    'Fiscal Management': 0,
    'Facilities': 0
  },
  recent_changes: 20,
  last_successful_scrape: '2025-06-07 14:07:05'
}

const mockPolicies = [
  { policy_code: '1000', title: 'Legal Status of the Board and School System', section: 'Governing Principles', version_number: 1 },
  { policy_code: '1010', title: 'Board Authority and Duties', section: 'Governing Principles', version_number: 1 },
  { policy_code: '1100', title: 'Governing Principles', section: 'Governing Principles', version_number: 1 },
  { policy_code: '1200', title: 'Governing Principle - Student Success', section: 'Governing Principles', version_number: 1 },
  { policy_code: '1300', title: 'Governing Principle - Parental Involvement', section: 'Governing Principles', version_number: 1 }
]

const mockSections = [
  { section_code: '1000', section_name: 'Governing Principles', color_code: '#FF6B6B', policy_count: 1 },
  { section_code: '2000', section_name: 'Board Operations', color_code: '#FFA500', policy_count: 0 },
  { section_code: '3000', section_name: 'Educational Program', color_code: '#32CD32', policy_count: 0 },
  { section_code: '4000', section_name: 'Students', color_code: '#FFD700', policy_count: 0 }
]

function PolicyContent({ content }) {
  // Parse and format policy content
  const formatContent = (text) => {
    if (!text) return null;
    
    // Split content into sections
    const sections = [];
    let currentSection = { type: 'text', content: '' };
    
    const lines = text.split(/(?<=[.!?])\s*(?=[A-Z])|(?=Legal References:|Cross References:|Adopted:|Revised:)/);
    
    lines.forEach(line => {
      line = line.trim();
      if (!line) return;
      
      if (line.startsWith('Policy Code:')) {
        sections.push({ type: 'header', content: line });
      } else if (line.startsWith('Legal References:')) {
        sections.push({ type: 'legal', content: line });
      } else if (line.startsWith('Cross References:')) {
        sections.push({ type: 'cross', content: line });
      } else if (line.startsWith('Adopted:') || line.startsWith('Revised:')) {
        sections.push({ type: 'date', content: line });
      } else if (line.endsWith('Pitt County Schools')) {
        sections.push({ type: 'footer', content: line });
      } else {
        if (currentSection.content) {
          currentSection.content += ' ' + line;
        } else {
          currentSection.content = line;
        }
        if (line.endsWith('.') || line.endsWith(':')) {
          sections.push({ ...currentSection });
          currentSection = { type: 'text', content: '' };
        }
      }
    });
    
    if (currentSection.content) {
      sections.push(currentSection);
    }
    
    return sections;
  };
  
  const sections = formatContent(content);
  
  return (
    <div className="prose max-w-none">
      {sections && sections.map((section, index) => {
        switch (section.type) {
          case 'header':
            return <h3 key={index} className="text-lg font-bold text-gray-900 mb-4">{section.content}</h3>;
          case 'legal':
            return (
              <div key={index} className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">Legal References</h4>
                <p className="text-sm text-blue-800">{section.content.replace('Legal References:', '').trim()}</p>
              </div>
            );
          case 'cross':
            return (
              <div key={index} className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Cross References</h4>
                <p className="text-sm text-gray-700">{section.content.replace('Cross References:', '').trim()}</p>
              </div>
            );
          case 'date':
            return (
              <div key={index} className="mt-4 text-sm text-gray-600">
                <p>{section.content}</p>
              </div>
            );
          case 'footer':
            return (
              <div key={index} className="mt-6 pt-4 border-t text-center text-sm text-gray-500">
                <p>{section.content}</p>
              </div>
            );
          default:
            return <p key={index} className="mb-4 text-gray-700 leading-relaxed">{section.content}</p>;
        }
      })}
    </div>
  );
}

function Header() {
  const [searchQuery, setSearchQuery] = useState('')
  const navigate = useNavigate()

  const handleSearch = (e) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`)
    }
  }

  return (
    <header className="border-b bg-white shadow-sm">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/" className="flex items-center space-x-2">
              <FileText className="h-8 w-8 text-blue-600" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Pitt County Schools</h1>
                <p className="text-sm text-gray-600">Policy Tracker</p>
              </div>
            </Link>
          </div>
          
          <nav className="hidden md:flex items-center space-x-6">
            <Link to="/" className="text-gray-700 hover:text-blue-600 flex items-center space-x-1">
              <Home className="h-4 w-4" />
              <span>Home</span>
            </Link>
            <Link to="/policies" className="text-gray-700 hover:text-blue-600 flex items-center space-x-1">
              <FileText className="h-4 w-4" />
              <span>Policies</span>
            </Link>
            <Link to="/changes" className="text-gray-700 hover:text-blue-600 flex items-center space-x-1">
              <GitBranch className="h-4 w-4" />
              <span>Changes</span>
            </Link>
            <Link to="/stats" className="text-gray-700 hover:text-blue-600 flex items-center space-x-1">
              <BarChart3 className="h-4 w-4" />
              <span>Statistics</span>
            </Link>
          </nav>

          <div className="flex items-center space-x-4">
            <form onSubmit={handleSearch} className="hidden md:flex">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search policies..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
            </form>
            
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="md:hidden">
                  <Menu className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent>
                <nav className="flex flex-col space-y-4 mt-8">
                  <Link to="/" className="text-gray-700 hover:text-blue-600 flex items-center space-x-2">
                    <Home className="h-4 w-4" />
                    <span>Home</span>
                  </Link>
                  <Link to="/policies" className="text-gray-700 hover:text-blue-600 flex items-center space-x-2">
                    <FileText className="h-4 w-4" />
                    <span>Policies</span>
                  </Link>
                  <Link to="/changes" className="text-gray-700 hover:text-blue-600 flex items-center space-x-2">
                    <GitBranch className="h-4 w-4" />
                    <span>Changes</span>
                  </Link>
                  <Link to="/stats" className="text-gray-700 hover:text-blue-600 flex items-center space-x-2">
                    <BarChart3 className="h-4 w-4" />
                    <span>Statistics</span>
                  </Link>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  )
}

function HomePage() {
  const [stats, setStats] = useState(mockStats)
  const [recentPolicies, setRecentPolicies] = useState([])
  const [loading, setLoading] = useState(true)
  
  const handleExportData = async () => {
    try {
      // Fetch all policies
      const response = await fetch(`${API_BASE}/policies`)
      const data = await response.json()
      
      if (data.policies) {
        // Create CSV content
        const csvHeader = 'Policy Code,Title,Section,Status,Version Count,Last Updated\n'
        const csvRows = data.policies.map(p => 
          `"${p.policy_code}","${p.title}","${p.section_name}","${p.status}","${p.version_count}","${p.updated_at}"`
        ).join('\n')
        
        const csvContent = csvHeader + csvRows
        
        // Create download
        const blob = new Blob([csvContent], { type: 'text/csv' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `pitt-county-policies-${new Date().toISOString().split('T')[0]}.csv`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      }
    } catch (error) {
      console.error('Error exporting data:', error)
      alert('Failed to export data. Please try again.')
    }
  }

  useEffect(() => {
    // Fetch stats
    Promise.all([
      fetch(`${API_BASE}/stats`).then(res => res.json()),
      fetch(`${API_BASE}/policies?limit=5`).then(res => res.json())
    ])
      .then(([statsData, policiesData]) => {
        if (statsData) {
          // Transform API response to match expected format
          const transformedStats = {
            total_policies: statsData.totalPolicies,
            policies_by_section: {},
            recent_changes: statsData.recentChanges,
            last_successful_scrape: statsData.lastScrape?.completed_at || new Date().toISOString()
          }
          
          // Convert policiesBySection array to object
          if (statsData.policiesBySection) {
            statsData.policiesBySection.forEach(section => {
              transformedStats.policies_by_section[section.section_name] = section.count
            })
          }
          
          setStats(transformedStats)
        }
        if (policiesData.policies) {
          setRecentPolicies(policiesData.policies.slice(0, 5).map(p => ({
            policy_code: p.policy_code,
            title: p.title,
            section: p.section_name,
            version_number: p.version_count
          })))
        }
        setLoading(false)
      })
      .catch(err => {
        console.error('Error fetching data:', err)
        setLoading(false)
      })
  }, [])

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Pitt County Schools Policy Tracker
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Track, search, and monitor changes to school board policies with comprehensive 
          version control and diff functionality, similar to congress.dev for educational governance.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Policies</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total_policies}</div>
            <p className="text-xs text-muted-foreground">
              Across all sections
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Recent Changes</CardTitle>
            <GitBranch className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.recent_changes}</div>
            <p className="text-xs text-muted-foreground">
              In the last 30 days
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Last Updated</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">Today</div>
            <p className="text-xs text-muted-foreground">
              {stats.last_successful_scrape}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card>
          <CardHeader>
            <CardTitle>Recent Policies</CardTitle>
            <CardDescription>
              Latest policies added to the system
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentPolicies.map((policy) => (
                <div key={policy.policy_code} className="flex items-center justify-between">
                  <div>
                    <Link 
                      to={`/policies/${policy.policy_code}`}
                      className="font-medium text-blue-600 hover:text-blue-800"
                    >
                      {policy.policy_code}: {policy.title}
                    </Link>
                    <p className="text-sm text-gray-600">{policy.section}</p>
                  </div>
                  <Badge variant="secondary">v{policy.version_number}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Policy Sections</CardTitle>
            <CardDescription>
              Policies organized by section
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(stats.policies_by_section).map(([section, count]) => (
                <div key={section} className="flex items-center justify-between">
                  <span className="text-sm font-medium">{section}</span>
                  <Badge variant={count > 0 ? "default" : "secondary"}>
                    {count} policies
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>
            Common tasks and exports
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <Link to="/policies">
              <Button>
                <FileText className="mr-2 h-4 w-4" />
                Browse All Policies
              </Button>
            </Link>
            <Link to="/changes">
              <Button variant="outline">
                <GitBranch className="mr-2 h-4 w-4" />
                View Recent Changes
              </Button>
            </Link>
            <Button variant="outline" onClick={handleExportData}>
              <Download className="mr-2 h-4 w-4" />
              Export Data
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function PoliciesPage() {
  const [policies, setPolicies] = useState([])
  const [sections, setSections] = useState([])
  const [selectedSection, setSelectedSection] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Fetch policies
    fetch(`${API_BASE}/policies`)
      .then(res => res.json())
      .then(data => {
        if (data.policies) {
          setPolicies(data.policies.map(p => ({
            policy_code: p.policy_code,
            title: p.title,
            section: p.section_name,
            version_number: p.version_count
          })))
          
          // Extract unique sections with proper counts
          const sectionMap = new Map()
          data.policies.forEach(p => {
            if (!sectionMap.has(p.section_name)) {
              sectionMap.set(p.section_name, {
                section_code: p.section_id.toString(),
                section_name: p.section_name,
                policy_count: 0
              })
            }
            sectionMap.get(p.section_name).policy_count++
          })
          
          setSections(Array.from(sectionMap.values()))
        }
        setLoading(false)
      })
      .catch(err => {
        console.error('Error fetching policies:', err)
        // Fall back to mock data
        setPolicies(mockPolicies)
        setSections(mockSections)
        setLoading(false)
      })
  }, [])

  const filteredPolicies = policies.filter(policy => {
    const matchesSection = selectedSection === 'all' || policy.section === selectedSection
    const matchesSearch = policy.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         policy.policy_code.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesSection && matchesSearch
  })

  if (loading) {
    return <div className="text-center py-8">Loading policies...</div>
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Policy Browser</h1>
        <p className="text-gray-600">Browse and search all school board policies</p>
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search policies by title or code..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <div className="md:w-64">
          <select
            value={selectedSection}
            onChange={(e) => setSelectedSection(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Sections</option>
            {sections.map(section => (
              <option key={section.section_code} value={section.section_name}>
                {section.section_name} ({section.policy_count})
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid gap-4">
        {filteredPolicies.map((policy) => (
          <Card key={policy.policy_code} className="hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <Link 
                    to={`/policies/${policy.policy_code}`}
                    className="text-lg font-semibold text-blue-600 hover:text-blue-800"
                  >
                    Policy {policy.policy_code}: {policy.title}
                  </Link>
                  <p className="text-gray-600 mt-1">{policy.section}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge variant="secondary">v{policy.version_number}</Badge>
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/policies/${policy.policy_code}`}>
                      View Details
                    </Link>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredPolicies.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No policies found</h3>
            <p className="text-gray-600">
              Try adjusting your search terms or section filter.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function PolicyDetailPage({ policyCode }) {
  const [policy, setPolicy] = useState(null)
  const [versions, setVersions] = useState([])
  const [loading, setLoading] = useState(true)
  
  const handleExportPolicy = (policy) => {
    if (!policy) return
    
    // Create text content with policy details
    const textContent = `Policy ${policy.policy_code}: ${policy.title}
Section: ${policy.section}
Version: ${policy.version_number}
Last Updated: ${policy.scraped_at}

${policy.content}
`
    
    // Create download
    const blob = new Blob([textContent], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `policy-${policy.policy_code}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  useEffect(() => {
    fetch(`${API_BASE}/policy/${policyCode}`)
      .then(res => res.json())
      .then(data => {
        if (data.policy && data.versions && data.versions.length > 0) {
          // Get the latest version's content
          const latestVersion = data.versions[0]
          setPolicy({
            ...data.policy,
            content: latestVersion.content,
            version_number: latestVersion.version_number,
            scraped_at: latestVersion.created_at,
            source_url: `https://www.boardpolicyonline.com/?b=pitt_co&s=${policyCode}`
          })
          setVersions(data.versions)
        }
        setLoading(false)
      })
      .catch(err => {
        console.error('Error fetching policy:', err)
        setLoading(false)
      })
  }, [policyCode])

  if (loading) {
    return <div className="text-center py-8">Loading policy...</div>
  }

  if (!policy) {
    return <div className="text-center py-8">Policy not found</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Policy {policy.policy_code}
          </h1>
          <h2 className="text-xl text-gray-700 mb-4">{policy.title}</h2>
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span>Section: {policy.section}</span>
            <span>Version: {policy.version_number}</span>
            <span>Updated: {policy.scraped_at}</span>
          </div>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" asChild>
            <a href={policy.source_url} target="_blank" rel="noopener noreferrer">
              View Original
            </a>
          </Button>
          <Button variant="outline" onClick={() => handleExportPolicy(policy)}>
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      <Tabs defaultValue="content" className="w-full">
        <TabsList>
          <TabsTrigger value="content">Policy Content</TabsTrigger>
          <TabsTrigger value="history">Version History</TabsTrigger>
          <TabsTrigger value="changes">Recent Changes</TabsTrigger>
        </TabsList>
        
        <TabsContent value="content" className="mt-6">
          <Card>
            <CardContent className="p-6">
              <PolicyContent content={policy.content} />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="history" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Version History</CardTitle>
              <CardDescription>
                All versions of this policy
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {versions.map((version, index) => (
                  <div key={version.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <div className="font-medium">
                        Version {version.version_number}
                        {index === 0 && ' (Current)'}
                      </div>
                      <div className="text-sm text-gray-600">
                        Created on {new Date(version.created_at).toLocaleDateString()}
                      </div>
                      {version.change_summary && (
                        <div className="text-sm text-gray-500 mt-1">
                          {version.change_summary}
                        </div>
                      )}
                    </div>
                    {index === 0 && <Badge>Current</Badge>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="changes" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Recent Changes</CardTitle>
              <CardDescription>
                Change history for this policy
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center space-x-4 p-4 border rounded-lg">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <div className="flex-1">
                    <div className="font-medium">Policy Created</div>
                    <div className="text-sm text-gray-600">
                      Initial version of policy added to system
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {policy.scraped_at}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

function App() {
  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/policies" element={<PoliciesPage />} />
            <Route path="/policies/:policyCode" element={<PolicyDetailWrapper />} />
            <Route path="/changes" element={<ChangesPage />} />
            <Route path="/stats" element={<StatsPage />} />
            <Route path="/search" element={<SearchResultsPage />} />
          </Routes>
        </main>
      </div>
    </Router>
  )
}

function PolicyDetailWrapper() {
  const { policyCode } = useParams()
  return <PolicyDetailPage policyCode={policyCode} />
}

function ChangesPage() {
  const [changes, setChanges] = useState([])
  const [loading, setLoading] = useState(true)
  
  useEffect(() => {
    // Fetch all policies with version counts > 1
    fetch(`${API_BASE}/policies`)
      .then(res => res.json())
      .then(data => {
        if (data.policies) {
          // Filter policies with multiple versions
          const changedPolicies = data.policies.filter(p => p.version_count > 1);
          setChanges(changedPolicies);
        }
        setLoading(false)
      })
      .catch(err => {
        console.error('Error fetching changes:', err)
        setLoading(false)
      })
  }, [])
  
  if (loading) {
    return <div className="text-center py-8">Loading changes...</div>
  }
  
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Recent Policy Changes</h1>
        <p className="text-gray-600">Track updates and revisions to school board policies</p>
      </div>
      
      {changes.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <GitBranch className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No recent changes</h3>
            <p className="text-gray-600">
              All policies are at their initial version.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {changes.map((policy) => (
            <Card key={policy.policy_code}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <Link 
                      to={`/policies/${policy.policy_code}`}
                      className="text-lg font-semibold text-blue-600 hover:text-blue-800"
                    >
                      Policy {policy.policy_code}: {policy.title}
                    </Link>
                    <p className="text-sm text-gray-600 mt-1">
                      {policy.section_name} • {policy.version_count} versions
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      Last updated: {new Date(policy.updated_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Badge variant="secondary">{policy.version_count} versions</Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}

function StatsPage() {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  
  useEffect(() => {
    fetch(`${API_BASE}/stats`)
      .then(res => res.json())
      .then(data => {
        if (data) {
          // Transform API response to match expected format
          const transformedStats = {
            total_policies: data.totalPolicies,
            policies_by_section: {},
            recent_changes: data.recentChanges,
            last_successful_scrape: data.lastScrape?.completed_at || new Date().toISOString()
          }
          
          // Convert policiesBySection array to object
          if (data.policiesBySection) {
            data.policiesBySection.forEach(section => {
              transformedStats.policies_by_section[section.section_name] = section.count
            })
          }
          
          setStats(transformedStats)
        }
        setLoading(false)
      })
      .catch(err => {
        console.error('Error fetching stats:', err)
        setLoading(false)
      })
  }, [])
  
  if (loading) {
    return <div className="text-center py-8">Loading statistics...</div>
  }
  
  if (!stats) {
    return <div className="text-center py-8">No statistics available</div>
  }
  
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Policy Statistics</h1>
        <p className="text-gray-600">Overview of the policy tracking system</p>
      </div>
      
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Total Policies</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">{stats.total_policies}</div>
            <p className="text-sm text-gray-600 mt-1">Active policies being tracked</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Recent Changes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">{stats.recent_changes}</div>
            <p className="text-sm text-gray-600 mt-1">Updates in the last 30 days</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Last Scrape</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-medium text-gray-900">
              {new Date(stats.last_successful_scrape).toLocaleDateString()}
            </div>
            <p className="text-sm text-gray-600 mt-1">
              {new Date(stats.last_successful_scrape).toLocaleTimeString()}
            </p>
          </CardContent>
        </Card>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Policies by Section</CardTitle>
          <CardDescription>Distribution of policies across different sections</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.entries(stats.policies_by_section).map(([section, count]) => (
              <div key={section} className="flex items-center justify-between">
                <span className="text-sm font-medium">{section}</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ width: `${(count / stats.total_policies) * 100}%` }}
                    />
                  </div>
                  <Badge variant="secondary">{count}</Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function SearchResultsPage() {
  const [searchParams] = useSearchParams()
  const query = searchParams.get('q') || ''
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(true)
  
  useEffect(() => {
    if (query) {
      fetch(`${API_BASE}/search?q=${encodeURIComponent(query)}`)
        .then(res => res.json())
        .then(data => {
          if (data.results) {
            setResults(data.results)
          }
          setLoading(false)
        })
        .catch(err => {
          console.error('Error searching:', err)
          setLoading(false)
        })
    } else {
      setLoading(false)
    }
  }, [query])
  
  if (!query) {
    return (
      <div className="text-center py-8">
        <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Enter a search query</h3>
        <p className="text-gray-600">Use the search bar above to find policies</p>
      </div>
    )
  }
  
  if (loading) {
    return <div className="text-center py-8">Searching for "{query}"...</div>
  }
  
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Search Results</h1>
        <p className="text-gray-600">
          Found {results.length} results for "{query}"
        </p>
      </div>
      
      {results.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
            <p className="text-gray-600">
              Try adjusting your search terms or browse policies by section.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {results.map((result) => (
            <Card key={result.policy_code}>
              <CardContent className="p-6">
                <Link 
                  to={`/policies/${result.policy_code}`}
                  className="text-lg font-semibold text-blue-600 hover:text-blue-800"
                >
                  Policy {result.policy_code}: {result.title}
                </Link>
                <p className="text-sm text-gray-600 mt-1">{result.section_name}</p>
                {result.snippet && (
                  <div 
                    className="mt-3 text-sm text-gray-700"
                    dangerouslySetInnerHTML={{ __html: result.snippet }}
                  />
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}

export default App

